import { createAuthClient } from 'better-auth/vue';

/**
 * Official Better Auth client (Vue bindings).
 *
 * The API serves Better Auth under basePath `/v1/auth` (see
 * apps/api/src/iam/iam.module.ts), while VITE_SUICH_API_URL points at
 * `/v1`. We derive the origin and let `basePath` build auth URLs.
 *
 * Session state lives in memory + the Better Auth session cookie
 * (server-side cookieCache is enabled) — nothing is persisted to
 * localStorage.
 */
const apiOrigin = new URL(import.meta.env.VITE_SUICH_API_URL).origin;

export const authClient = createAuthClient({
  baseURL: apiOrigin,
  basePath: '/v1/auth',
});

export type AdminRole = 'ADMIN' | 'SUPER_ADMIN';

interface AdminSessionUserShape {
  id: string;
  email: string;
  name?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  role?: string | null;
  profileId?: string | null;
  image?: string | null;
}

interface AdminSessionShape {
  id: string;
  userId: string;
  expiresAt: string | Date;
}

type SessionPayload = {
  session: AdminSessionShape;
  user: AdminSessionUserShape;
} | null;

/** Narrow a raw Better Auth user to the admin role, or null when absent/unknown. */
export function readAdminRole(user: unknown): AdminRole | null {
  const role = (user as AdminSessionUserShape | null | undefined)?.role;
  return role === 'ADMIN' || role === 'SUPER_ADMIN' ? role : null;
}

/** Reactive singleton session context: { data: SessionPayload, isPending, error, refetch } */
export function useAuthSession() {
  return authClient.useSession();
}

export type { SessionPayload };
